import { effect, inject, Injectable, signal } from '@angular/core';
import { TestingAccessService } from './testing-access.service';

interface PaymentDateOverrideState {
    isExpanded: boolean;
    uwAt: string | null;
    mortgageStartDate: string | null;
}

const DEFAULT_STATE: PaymentDateOverrideState = {
    isExpanded: false,
    uwAt: null,
    mortgageStartDate: null,
};

@Injectable({
    providedIn: 'root',
})
export class PaymentDateOverrideService {
    private readonly STORAGE_KEY = 'isi-dtc-payment-date-override';
    private testingAccess = inject(TestingAccessService);

    isExpanded = signal<boolean>(DEFAULT_STATE.isExpanded);
    uwAtOverride = signal<string | null>(DEFAULT_STATE.uwAt);
    mortgageStartDateOverride = signal<string | null>(DEFAULT_STATE.mortgageStartDate);

    constructor() {
        if (this.testingAccess.isEnabled()) {
            this.initializeState();

            effect(() => {
                const state: PaymentDateOverrideState = {
                    isExpanded: this.isExpanded(),
                    uwAt: this.uwAtOverride(),
                    mortgageStartDate: this.mortgageStartDateOverride(),
                };
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
            });
        }
    }

    toggleExpanded(): void {
        this.isExpanded.update((v) => !v);
    }

    setUwAt(value: string | null): void {
        this.uwAtOverride.set(value);
    }

    setMortgageStartDate(value: string | null): void {
        this.mortgageStartDateOverride.set(value);
    }

    private initializeState(): void {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) return;

        try {
            const state: PaymentDateOverrideState = JSON.parse(stored);
            this.isExpanded.set(state.isExpanded ?? false);
            this.uwAtOverride.set(state.uwAt ?? null);
            this.mortgageStartDateOverride.set(state.mortgageStartDate ?? null);
        } catch {
            // Invalid stored state, use defaults
        }
    }
}
